import { OpticalgymVueI18n, Lang } from "shared/i18n";
import type { ExerciseConfigContext } from "../lib/exercise_config_context";
// @ts-ignore - nunjucks doesn't have TypeScript definitions
import nunjucks from 'nunjucks';

// Global nunjucks environment for rendering exercise score templates
const nunjucks_env = new nunjucks.Environment();

/**
 * Get exercise default config with app-specific overrides applied.
 *
 * - Starts from global defaults (exercise.global)
 * - Applies app-specific overrides (exercise.app_config[appId]) if present
 * - Optionally overlays level-specific defaults when a level is provided
 * - When a context is given, overlays that context's defaults (exercise.context_params[context])
 *
 * If appId is null, undefined, or empty, returns global (plus optional level)
 * with no app-specific overrides.
 *
 * @param context Where the config is being built. That context's defaults are applied last; they
 *   are defaults like any other, so whoever edits the config can still change them. When omitted,
 *   no context defaults are applied.
 */
export function getExerciseDefaultConfig(
  exercise: {
    global?: Record<string, any>;
    app_config?: Record<string, Record<string, any>>;
    context_params?: Partial<Record<ExerciseConfigContext, Record<string, any>>>;
    num_levels?: number;
  },
  appId: string | null | undefined,
  level?: number | null,
  context?: ExerciseConfigContext
): Record<string, any> {
  const global = exercise.global || {};
  let result: Record<string, any> = JSON.parse(JSON.stringify(global));

  if (appId) {
    const appOverrides = exercise.app_config?.[appId];
    if (appOverrides) {
      Object.assign(result, appOverrides);
    }
  }

  // Optionally overlay level-specific defaults
  if (
    level != null &&
    typeof level === "number" &&
    level >= 1 &&
    (exercise.num_levels == null || level <= exercise.num_levels)
  ) {
    const levelConfig = (exercise as any)[`level${level}`];
    if (levelConfig && typeof levelConfig === "object") {
      result = {
        ...result,
        ...levelConfig,
        level,
      };
    }
  }

  const contextParams = context ? exercise.context_params?.[context] : undefined;
  if (contextParams) {
    Object.assign(result, JSON.parse(JSON.stringify(contextParams)));
  }

  return result;
}

export default interface Exercise {
  id: string;
  version: number;
  title: { [key: string]: string };
  group: string[];
  demo_only: boolean;
  deprecated: boolean;
  experimental: boolean;
  canary?: boolean;
  requires_calibration: boolean;
  speech_to_text?: boolean;
  speech_to_text_variants?: string[] | null;
  disable_fullscreen_ios: boolean;
  config_version: number;
  num_levels: number;
  level_deprecated?: LevelDeprecation | null;
  description: { [key: string]: string };
  has_launch_config: boolean;
  instructions: { [key: string]: string };
  guide: { [key: string]: string };
  level_hint: { [key: string]: string };
  global: { [key: string]: any };
  app_config?: Record<string, Record<string, any>>;
  // When true, this exercise can be rendered as a printable PDF sheet. v2 exercises only.
  printable?: boolean;
  // Param defaults per config context. Defaults, not fixed values: see getExerciseDefaultConfig.
  context_params?: Partial<Record<ExerciseConfigContext, Record<string, any>>>;
  reporting: { [key: string]: ExerciseFieldDisplay };
  labels: { [key: string]: ExerciseLabel };
  variants: { [key: string]: ExerciseVariant };
  level1: { [key: string]: any } | null;
  level2: { [key: string]: any } | null;
  level3: { [key: string]: any } | null;
  level4: { [key: string]: any } | null;
  level5: { [key: string]: any } | null;
  level6: { [key: string]: any } | null;
  level7: { [key: string]: any } | null;
  level8: { [key: string]: any } | null;
  level9: { [key: string]: any } | null;
  level10: { [key: string]: any } | null;
}

export type LevelDeprecation = "partial" | "full";

export interface ExerciseLabel {
  label: { [key: string]: string }; // Language -> label
  unit: { [key: string]: string }; // Language -> unit
}

export interface ExerciseVariant {
  title: { [key: string]: string };
  copy_to_title?: boolean;
  [key: string]: any;
}


interface ExerciseFieldDisplay {
  // Score Title in langcode -> title format
  title: { [key in Lang ]: string };

  // Display type for this field
  display: FieldDisplayType;

  // Should this field be displayed in the score summary
  summary?: boolean;

  // Should this field be displayed in the graph
  graph?: boolean;

  // Graph axis label in langcode -> label format
  axis?: { [key in Lang ]: string };

  // Suffix for the value in langcode -> suffix format
  suffix?: { [key in Lang ]: string };

  // If set, this field should only display if the specified field is non-zero / non-empty
  // Set to self to display only if this field is non-zero / non-empty
  display_if?: string | null;

  // If the display field is "Label", this is the value labels map for the field
  value_labels?: { [key: string ]: { [key in Lang ]: string } };

  // If the display field is "Fraction", this is the denominator field for the fraction
  fraction_denominator?: string | null;
}

enum FieldDisplayType {
  // It's a text value that maps to value_labels for display
  Label = "label",

  // A simple numeric integer value
  Integer = "integer",

  // Decimal value with two decimal places
  Decimal2 = "decimal_2",

  // A fraction, the denominator is in the "fraction_denominator" field
  Fraction = "fraction",

  // A percent, the value is stored as a float from 0-1, but is displayed as a percentage
  Percent = "percent",

  // A fraction, the denominator is in the "fraction_denominator" field
  // Also displays the value as a percentage (e.g., 1/4 (25%))
  FractionWithPercent = "fraction_with_percent",

  // A boolean value, displayed as true or false
  Boolean = "boolean",

  // A boolean value, displayed as the label with no true/false value displayed
  // If false, the label is not displayed
  BooleanLabelOnly = "boolean_label_only",

  // Value is milliseconds, displayed as MM:SS
  MsToMinutesSeconds = "ms_to_minutes_seconds",

  // Value is milliseconds, displayed as seconds (S)
  MsToSeconds = "ms_to_seconds",

  // Value is milliseconds, displayed as seconds with one decimal place (S.S)
  MsToDecimal1Seconds = "ms_to_decimal_1_seconds",

  // Value is milliseconds, displayed as seconds with two decimal places (S.SS)
  MsToDecimal2Seconds = "ms_to_decimal_2_seconds",

  // Vergence in prism diopters, BI convention
  VergenceBI = "vergence_bi",

  // Vergence in prism diopters, BO convention
  VergenceBO = "vergence_bo",

  // Vertical vergence in prism diopters, using configured eye and BU/BD direction
  VergenceBU = "vergence_bu",

  // Vertical vergence in prism diopters, using configured eye and BD/BU direction
  VergenceBD = "vergence_bd",

  // Horizontal phoria in prism diopters: positive Eso, negative Exo
  PhoriaHorizontal = "phoria_horizontal",

  // Vertical phoria in prism diopters: positive OD Hyper, negative OD Hypo
  PhoriaVertical = "phoria_vertical",
}

// Utility function to display exercise score
export function exercise_score_display(
  exercise: Exercise,
  display_field: string,
  score: { [key: string]: any },
  config: { [key: string]: any } | null | undefined,
  i18n: OpticalgymVueI18n, 
  include_suffix: boolean = true
): string {
    const field_config = exercise.reporting?.[display_field] ?? null;

    if (!field_config) {
      return "";
    }

    const field_value = score[display_field];

    const isNumericDisplay = (display: string) => {
      return [
        "integer",
        "decimal_2",
        "vergence_bi",
        "vergence_bo",
        "vergence_bu",
        "vergence_bd",
        "phoria_horizontal",
        "phoria_vertical",
        "fraction",
        "percent",
        "fraction_with_percent",
        "ms_to_minutes_seconds",
        "ms_to_seconds",
        "ms_to_decimal_1_seconds",
        "ms_to_decimal_2_seconds",
      ].includes(display);
    };

    // For numeric displays, treat null/undefined/NaN as "N/A"
    if (isNumericDisplay(field_config.display)) {
      const is_null_or_undefined = field_value === null || field_value === undefined;
      const numeric_value = Number(field_value);
      const is_nan = Number.isNaN(numeric_value);

      if (is_null_or_undefined || is_nan) {
        return "N/A";
      }
    }

    let suffix = "";
    if (
      include_suffix &&
      field_config.suffix &&
      field_config.display !== "vergence_bi" &&
      field_config.display !== "vergence_bo" &&
      field_config.display !== "vergence_bu" &&
      field_config.display !== "vergence_bd" &&
      field_config.display !== "phoria_horizontal" &&
      field_config.display !== "phoria_vertical"
    ) {
      suffix = field_config.suffix[i18n.lang()] ?? "";
      if (suffix) {
        // Render suffix through nunjucks template
        const context = {
          exercise: exercise,
          field_id: display_field,
          score: score,
          config: config || {},
        };
        
        // Add translation function to context
        const renderContext = {
          ...context,
          t: (key: string) => i18n.t(key),
        };

        try {
          suffix = nunjucks_env.renderString(suffix, renderContext);
        } catch (error) {
          console.error('Error rendering nunjucks suffix template:', error);
          // Fall back to original suffix if rendering fails
        }
        
        suffix = " " + suffix;
      }
    }

    switch (field_config.display) {
      case "label":
        // Ensure value_labels and field_value exist
        if (field_config.value_labels && field_config.value_labels[field_value]) {
          let label_display = field_config.value_labels[field_value][i18n.lang()] || "";
          if (label_display) {
            return label_display + suffix;
          }
          else {
            return field_value;
          }
        }
        return field_value;

      case "integer":
        if (field_value) {
          return String(Math.round(Number(field_value))) + suffix;
        } else {
          return "0" + suffix;
        }

      case "decimal_2":
        return Number(field_value).toFixed(2) + suffix;

      case "vergence_bi": {
        const valueNum = Number(field_value);
        const absValue = Math.abs(valueNum);
        const baseInShort = String(i18n.t("common.base_in_short"));
        const baseOutShort = String(i18n.t("common.base_out_short"));
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue.toFixed(2)} Δ ${baseInShort}`;
        } else {
          return `${absValue.toFixed(2)} Δ ${baseOutShort}`;
        }
      }

      case "vergence_bo": {
        const valueNum = Number(field_value);
        const absValue = Math.abs(valueNum);
        const baseInShort = String(i18n.t("common.base_in_short"));
        const baseOutShort = String(i18n.t("common.base_out_short"));
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue.toFixed(2)} Δ ${baseOutShort}`;
        } else {
          return `${absValue.toFixed(2)} Δ ${baseInShort}`;
        }
      }

      case "vergence_bu": {
        const valueNum = Number(field_value);
        const absValue = Math.abs(valueNum);
        const verticalEyeValue = config?.vertical_eye || config?.randot_vertical;
        const verticalEye = verticalEyeValue
          ? `${String(verticalEyeValue).toUpperCase()} `
          : "";
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue.toFixed(2)} Δ ${verticalEye} BU`;
        } else {
          return `${absValue.toFixed(2)} Δ ${verticalEye} BD`;
        }
      }

      case "vergence_bd": {
        const valueNum = Number(field_value);
        const absValue = Math.abs(valueNum);
        const verticalEyeValue = config?.vertical_eye || config?.randot_vertical;
        const verticalEye = verticalEyeValue
          ? `${String(verticalEyeValue).toUpperCase()} `
          : "";
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue.toFixed(2)} Δ ${verticalEye} BD`;
        } else {
          return `${absValue.toFixed(2)} Δ ${verticalEye} BU`;
        }
      }

      case "phoria_horizontal": {
        const valueNum = Number(field_value);
        const absValue = Number(Math.abs(valueNum).toFixed(2));
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue} Δ Eso`;
        } else {
          return `${absValue} Δ Exo`;
        }
      }

      case "phoria_vertical": {
        const valueNum = Number(field_value);
        const absValue = Number(Math.abs(valueNum).toFixed(2));
        if (valueNum === 0) {
          return "0 Δ";
        } else if (valueNum > 0) {
          return `${absValue} Δ OD Hyper`;
        } else {
          return `${absValue} Δ OD Hypo`;
        }
      }

      case "fraction":
        // Ensure fraction_denominator exists
        if (
          field_config.fraction_denominator &&
          score[field_config.fraction_denominator]
        ) {
          if (field_value) {
            const fraction_display = `${field_value}/${score[field_config.fraction_denominator]}`;
            return fraction_display + suffix;
          } else {
            return "0" + suffix;
          }
        }
        return field_value;

      case "percent":
        const percent_display = `${(Number(field_value) * 100).toFixed(0)}%`;
        return percent_display + suffix;

      case "fraction_with_percent":
        // Ensure fraction_denominator exists
        if (
          field_config.fraction_denominator &&
          score[field_config.fraction_denominator]
        ) {
          if (field_value) {
            let percent_value = Number(field_value) / Number(score[field_config.fraction_denominator]);
            return `${field_value}/${score[field_config.fraction_denominator]}${suffix} (${(percent_value * 100).toFixed(0)}%)`;
          } else {
            return "0" + suffix;
          }
        }
        return field_value;

        case "boolean":
          if (typeof field_value === "string") {
            return field_value === "true" ? String(i18n.t("common.boolean_true")) : String(i18n.t("common.boolean_false"));
          }
          else if (typeof field_value === "boolean") {
            return field_value ? String(i18n.t("common.boolean_true")) : String(i18n.t("common.boolean_false"));
          }

      case "boolean_label_only":
        // Label-only never displays score value
        return "";

      case "ms_to_minutes_seconds": {
        const valueInMs = Number(field_value);
        const minutes = Math.floor(valueInMs / 60000);
        const seconds = Math.floor((valueInMs % 60000) / 1000);
        return `${minutes < 10 ? "0" : ""}${minutes}:${
          seconds < 10 ? "0" : ""
        }${seconds}${suffix}`;
      }

      case "ms_to_seconds":
        return String(Math.round(Number(field_value) / 1000)) + suffix;

      case "ms_to_decimal_1_seconds":
        return String(Math.round(Number(field_value) / 100) / 10) + suffix;

      case "ms_to_decimal_2_seconds": {
        const seconds_2 = (Number(field_value) / 1000).toFixed(2);
        const ms_to_decimal_2_seconds_dispaly = Number(seconds_2) < 10 ? `0${seconds_2}` : `${seconds_2}`;
        return ms_to_decimal_2_seconds_dispaly + suffix;
      }

      default:
        return "";
    }
}
